/** US-1001: keep UI language and server profile.locale in sync for logged-in users. */

import i18n, { resolveAppLanguage, type AppLanguage } from "../i18n";
import keycloak from "../keycloak";
import { getUserProfile, patchUserProfile } from "../api";

let hydrateInFlight: Promise<AppLanguage | null> | null = null;
let hydratedForSub: string | null = null;

export async function persistLocaleToProfile(locale: AppLanguage): Promise<void> {
  if (!keycloak.authenticated) {
    return;
  }
  try {
    await patchUserProfile({ locale });
  } catch {
    // Offline / API errors must not block the UI language switch.
  }
}

/**
 * Once per authenticated user session: load profile.locale and apply to i18n.
 * Falls back to existing i18n/localStorage language when profile default is still `en`
 * and the UI already has a non-en choice — prefer profile when it is set and differs.
 */
export async function hydrateLocaleFromProfile(): Promise<AppLanguage | null> {
  if (!keycloak.authenticated) {
    return null;
  }
  const sub = keycloak.subject || keycloak.tokenParsed?.sub || "unknown";
  if (hydratedForSub === sub) {
    return resolveAppLanguage(i18n.language);
  }
  if (hydrateInFlight) {
    return hydrateInFlight;
  }
  hydrateInFlight = (async () => {
    try {
      const profile = await getUserProfile();
      const profileLocale = resolveAppLanguage(profile.locale);
      const current = resolveAppLanguage(i18n.language);
      // Profile still at default `en` while UI already has another language
      // (e.g. chosen on /welcome before login) — push local → server.
      if (profileLocale === "en" && current !== "en") {
        await patchUserProfile({ locale: current });
        hydratedForSub = sub;
        return current;
      }
      if (profileLocale !== current) {
        await i18n.changeLanguage(profileLocale);
      }
      hydratedForSub = sub;
      return profileLocale;
    } catch {
      return null;
    } finally {
      hydrateInFlight = null;
    }
  })();
  return hydrateInFlight;
}

/** Test helper — reset session hydrate memo. */
export function resetLocaleHydrationState(): void {
  hydrateInFlight = null;
  hydratedForSub = null;
}
